import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import i18next from "i18next";

const VIEW_CONFIG = {
  actual: { label: "Real", position: "position", prefix: "" },
  expected: {
    label: "Expected",
    position: "expected_position",
    prefix: "expected_",
  },
  should: { label: "Should", position: "should_position", prefix: "should_" },
};

const VIEW_OPTIONS = [
  ["actual", "Real"],
  ["expected", "Expected"],
  ["should", "Should"],
  ["should_actual", "Should − Real"],
  ["should_expected", "Should − Expected"],
  ["actual_expected", "Real − Expected"],
];

const PRIMARY_VIEW_OPTIONS = VIEW_OPTIONS.slice(0, 3);
const COMPARISON_VIEW_OPTIONS = VIEW_OPTIONS.slice(3);
const CLASSIC_TABLE_COLUMNS =
  "26px 28px minmax(105px,1fr) 34px 28px 28px 28px 76px 56px 44px";
const COMPARISON_TABLE_COLUMNS =
  "26px 28px minmax(125px,1fr) 66px 66px 54px";

const ruleAccent = (rules = []) => {
  const categories = new Set(rules.map((rule) => rule?.rule_category));
  if (categories.has("relegation")) return "#c65f68";
  if (categories.has("relegation_playoff") || categories.has("relegation_round")) return "#d99558";
  if (categories.has("promotion") || categories.has("championship_round")) return "#5c9f76";
  if (categories.has("promotion_playoff")) return "#6f92bd";
  return rules.length ? "#8d7aa7" : "transparent";
};

export function prepareCompactLeagueRows(rows, view, teamNames = new Map()) {
  return prepareLeagueRowsForScope(rows, view, "overall", teamNames);
}

export function prepareLeagueRowsForScope(rows, view, scope = "overall", teamNames = new Map()) {
  const config = VIEW_CONFIG[view] || VIEW_CONFIG.actual;
  return (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const values = row?.[scope] || {};
      const read = (field) => Number(values[`${config.prefix}${field}`]);
      const goalsScored = read("goals_scored");
      const goalsAgainst = read("goals_against");
      const played = read("games_played");
      const goalSum = goalsScored + goalsAgainst;
      const scopedRate = (scope, field) => {
        const scopedValues = row?.[scope] || {};
        const games = Number(scopedValues[`${config.prefix}games_played`]);
        const value = Number(scopedValues[`${config.prefix}${field}`]);
        return games > 0 && Number.isFinite(value) ? value / games : null;
      };
      const forHome = scopedRate("home", "goals_scored");
      const againstHome = scopedRate("home", "goals_against");
      const forAway = scopedRate("away", "goals_scored");
      const againstAway = scopedRate("away", "goals_against");
      const forTotal = Number.isFinite(forHome) && Number.isFinite(forAway)
        ? (forHome + forAway) / 2
        : null;
      const againstTotal = Number.isFinite(againstHome) && Number.isFinite(againstAway)
        ? (againstHome + againstAway) / 2
        : null;
      const scopedSummary = (scope) => {
        const scopedValues = row?.[scope] || {};
        const value = (field) => Number(scopedValues[`${config.prefix}${field}`]);
        const scopedGoalsScored = value("goals_scored");
        const scopedGoalsAgainst = value("goals_against");
        const storedDifference = value("goal_difference");
        return {
          played: value("games_played"),
          points: value("points"),
          goalsScored: scopedGoalsScored,
          goalsAgainst: scopedGoalsAgainst,
          difference: Number.isFinite(storedDifference) ? storedDifference : Number.isFinite(scopedGoalsScored) && Number.isFinite(scopedGoalsAgainst) ? scopedGoalsScored - scopedGoalsAgainst : null,
        };
      };
      return {
        teamId: Number(row?.team_id),
        name:
          row?.team_name ||
          teamNames.get(Number(row?.team_id)) ||
          `Team ${row?.team_id}`,
        position: Number(row?.[config.position]),
        played,
        won: read("won"),
        draw: read("draw"),
        lost: read("lost"),
        goalsScored,
        goalsAgainst,
        goalSum,
        goalAverage: played > 0 ? goalSum / played : null,
        difference: read("goal_difference"),
        quotient:
          goalsAgainst === 0
            ? Infinity
            : goalsScored / goalsAgainst,
        points: read("points"),
        home: scopedSummary("home"),
        away: scopedSummary("away"),
        fixtureSettingValues: {
          total_strength:
            Number.isFinite(forTotal) && Number.isFinite(againstTotal) && againstTotal > 0
              ? forTotal / againstTotal : null,
          total_goals:
            Number.isFinite(forTotal) && Number.isFinite(againstTotal)
              ? forTotal + againstTotal : null,
          for_total: forTotal,
          against_total: againstTotal,
          for_home: forHome,
          against_home: againstHome,
          for_away: forAway,
          against_away: againstAway,
          for_homefactor:
            Number.isFinite(forHome) && Number.isFinite(forAway) && (forHome + forAway) > 0
              ? forHome / ((forHome + forAway) / 2)
              : null,
          against_homefactor:
            Number.isFinite(againstHome) && Number.isFinite(againstAway) && (againstHome + againstAway) > 0
              ? againstHome / ((againstHome + againstAway) / 2)
              : null,
          draw_factor: null,
        },
      };
    })
    .sort((a, b) =>
      (Number.isFinite(b.points) ? b.points : -Infinity) -
        (Number.isFinite(a.points) ? a.points : -Infinity) ||
      (Number.isFinite(b.difference) ? b.difference : -Infinity) -
        (Number.isFinite(a.difference) ? a.difference : -Infinity) ||
      (Number.isFinite(b.goalsScored) ? b.goalsScored : -Infinity) -
        (Number.isFinite(a.goalsScored) ? a.goalsScored : -Infinity) ||
      a.name.localeCompare(b.name)
    );
}

export const signedDifference = (number, digits = 1) => {
  if (!Number.isFinite(number)) return "–";
  const formatted = number.toFixed(digits);
  return number > 0 ? `+${formatted}` : formatted;
};

export function prepareCompactComparisonRows(
  leftRows,
  leftView,
  rightRows,
  rightView,
  teamNames,
  metric = "points",
  scope = "overall"
) {
  const left = prepareLeagueRowsForScope(leftRows, leftView, scope, teamNames);
  const rightByTeam = new Map(
    prepareLeagueRowsForScope(rightRows, rightView, scope, teamNames)
      .map((row) => [row.teamId, row])
  );
  return left
    .map((row) => {
      const other = rightByTeam.get(row.teamId);
      const leftValue = row?.[metric];
      const rightValue = other?.[metric];
      return {
        ...row,
        leftValue,
        rightValue,
        delta:
          Number.isFinite(leftValue) && Number.isFinite(rightValue)
            ? leftValue - rightValue
            : null,
      };
    })
    .sort((a, b) =>
      (Number.isFinite(b.delta) ? b.delta : -Infinity) -
        (Number.isFinite(a.delta) ? a.delta : -Infinity) ||
      a.name.localeCompare(b.name)
    );
}

const tableValue = (number, view) =>
  Number.isFinite(number) ? number.toFixed(view === "actual" ? 0 : 2) : "–";

const comparisonValue = (number, view) =>
  Number.isFinite(number) ? number.toFixed(view === "actual" ? 0 : 2) : "–";

const comparisonDelta = (number) => {
  if (!Number.isFinite(number)) return "–";
  if (number > 0) return `+${number.toFixed(2)}`;
  if (number < 0) return `−${Math.abs(number).toFixed(2)}`;
  return "0.00";
};

export function sortCompactRows(rows, key, direction = "desc") {
  return [...rows].sort((a, b) => {
    const aValue = a?.[key] == null ? null : Number(a[key]);
    const bValue = b?.[key] == null ? null : Number(b[key]);
    const aNumeric = aValue !== null && !Number.isNaN(aValue);
    const bNumeric = bValue !== null && !Number.isNaN(bValue);
    if (!aNumeric || !bNumeric) return aNumeric ? -1 : bNumeric ? 1 : 0;
    if (aValue === bValue) return 0;
    if (direction === "desc") return aValue < bValue ? 1 : -1;
    return aValue < bValue ? -1 : 1;
  });
}

export const addCompactRanks = (rows) =>
  rows.map((row, index) => ({ ...row, displayRank: index + 1 }));

export function CompactLeagueTable({
  actualRows,
  expectedRows,
  shouldRows,
  selectedTeamIds = [],
  seasonId,
  onViewChange,
  teamLogos = new Map(),
  standingRules = [],
  teamSettings = null,
}) {
  const [view, setView] = useState("actual");
  const [scope, setScope] = useState("overall");
  const [analysisMode, setAnalysisMode] = useState("table");
  const [comparisonMetric, setComparisonMetric] = useState("points");
  const [goalView, setGoalView] = useState("goals");
  const [performanceView, setPerformanceView] = useState("difference");
  const [sort, setSort] = useState({ key: "points", direction: "desc" });
  useEffect(() => {
    setView("actual");
    setScope("overall");
    setAnalysisMode("table");
    setComparisonMetric("points");
    setGoalView("goals");
    setPerformanceView("difference");
    setSort({ key: "points", direction: "desc" });
  }, [seasonId]);

  const teamNames = useMemo(
    () =>
      new Map(
        (actualRows || []).map((row) => [Number(row.team_id), row.team_name])
      ),
    [actualRows]
  );
  const sources = {
    actual: actualRows,
    expected: expectedRows,
    should: shouldRows,
  };
  const comparisonViews = view.split("_");
  const isComparison = comparisonViews.length === 2;
  const preparedRows = isComparison
    ? prepareCompactComparisonRows(
        sources[comparisonViews[0]],
        comparisonViews[0],
        sources[comparisonViews[1]],
        comparisonViews[1],
        teamNames,
        comparisonMetric,
        scope
      )
    : prepareLeagueRowsForScope(sources[view], view, scope, teamNames);
  const rows = addCompactRanks(
    sortCompactRows(preparedRows, sort.key, sort.direction)
  );
  const selected = new Set(selectedTeamIds.map(Number));
  const rulesByTeam = useMemo(() => {
    const map = new Map();
    for (const rule of standingRules || []) {
      const id = Number(rule?.team_id);
      if (Number.isFinite(id)) map.set(id, [...(map.get(id) || []), rule]);
    }
    return map;
  }, [standingRules]);
  const settingFor = (teamId) => teamSettings?.[String(teamId)] || teamSettings?.[teamId] || null;
  const allMetricColumns = [
    ["total_strength", "Strength"], ["total_goals", "Goals Sum"],
    ["for_total", "For total"], ["against_total", "Against total"],
    ["for_home", "For home"], ["against_home", "Against home"],
    ["for_away", "For away"], ["against_away", "Against away"],
    ["for_homefactor", "For homefactor"], ["against_homefactor", "Against homefactor"],
    ["draw_factor", "Draw factor"],
  ];
  const metricColumns = analysisMode === "strength" ? allMetricColumns.slice(0, 1)
    : analysisMode === "goals" ? allMetricColumns.slice(1, 2)
      : analysisMode === "homeaway" ? allMetricColumns.slice(4, 8)
        : analysisMode === "factors" ? allMetricColumns.slice(8)
          : allMetricColumns;

  const changeSort = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));
  };

  const sortLabel = (label, key) => (
    <Box
      component="button"
      type="button"
      onClick={() => changeSort(key)}
      sx={{
        p: 0,
        border: 0,
        bgcolor: "transparent",
        color: "inherit",
        font: "inherit",
        fontWeight: "inherit",
        textAlign: "left",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {label}{sort.key === key ? (sort.direction === "desc" ? " ↓" : " ↑") : ""}
    </Box>
  );

  const modeHeader = (value, onChange, options, sortKey, sortable = true) => (
    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
      <Box
        component="select"
        value={value}
        onChange={onChange}
        sx={{
          minWidth: 0,
          maxWidth: "100%",
          border: 0,
          bgcolor: "transparent",
          color: "inherit",
          font: "inherit",
          fontWeight: "inherit",
          cursor: "pointer",
        }}
      >
        {options.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </Box>
      {sortable && (
        <Box
          component="button"
          type="button"
          aria-label={`Sort ${value}`}
          onClick={() => changeSort(sortKey)}
          sx={{ p: 0, border: 0, bgcolor: "transparent", color: "inherit", font: "inherit", cursor: "pointer" }}
        >
          {sort.key === sortKey ? (sort.direction === "desc" ? "↓" : "↑") : "↕"}
        </Box>
      )}
    </Box>
  );

  const goalSortKey = goalView === "sum" ? "goalSum" : "goalAverage";
  const performanceSortKey = performanceView === "difference" ? "difference" : "quotient";
  const numeric = (number, digits = 2) =>
    Number.isFinite(number) ? number.toFixed(digits) : "–";

  const selectView = (next) => {
    setView(next);
    onViewChange?.(next);
    setSort({ key: next.includes("_") ? "delta" : "points", direction: "desc" });
  };

  const comparisonAvailable = COMPARISON_VIEW_OPTIONS.some(([key]) =>
    key.split("_").every((source) => sources[source]?.length)
  );

  return (
    <Box sx={{ minWidth: 0, color: "#17242d" }} data-testid="compact-league-table">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          {i18next.t("help.leagueWorkspace.leagueTable")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.65, flexWrap: "wrap" }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={isComparison ? "comparison" : view}
            onChange={(_, next) => {
              if (!next) return;
              if (next === "comparison") {
                const first = COMPARISON_VIEW_OPTIONS.find(([key]) => key.split("_").every((source) => sources[source]?.length));
                if (first) selectView(first[0]);
              } else selectView(next);
            }}
            aria-label={i18next.t("help.tableStats.display")}
            sx={{ "& .MuiToggleButton-root": { px: 1.2, py: 0.35, fontSize: "0.7rem", fontWeight: 900 } }}
          >
            {PRIMARY_VIEW_OPTIONS.map(([key, label]) => (
              <ToggleButton key={key} value={key} disabled={!sources[key]?.length}>{label}</ToggleButton>
            ))}
            <ToggleButton value="comparison" disabled={!comparisonAvailable}>Vergleich</ToggleButton>
          </ToggleButtonGroup>
          {isComparison && (
            <Box
              component="select"
              data-testid="comparison-view-select"
              aria-label="Vergleich auswählen"
              value={view}
              onChange={(event) => selectView(event.target.value)}
              sx={{ height: 30, border: "1px solid", borderColor: "divider", borderRadius: 1, px: 0.8, bgcolor: "white", fontSize: "0.7rem", fontWeight: 800 }}
            >
              {COMPARISON_VIEW_OPTIONS.map(([key, label]) => (
                <option key={key} value={key} disabled={key.split("_").some((source) => !sources[source]?.length)}>{label}</option>
              ))}
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 1.1, flexWrap: "wrap", p: 0.6, borderRadius: 1.5, bgcolor: "#edf1f4" }}>
        <ToggleButtonGroup exclusive size="small" value={scope} onChange={(_, value) => value && setScope(value)} sx={{ "& .MuiToggleButton-root": { px: 1.35, py: 0.3, fontSize: "0.7rem", fontWeight: 900 } }}>
          <ToggleButton value="overall">Gesamt</ToggleButton>
          <ToggleButton value="home">Heim</ToggleButton>
          <ToggleButton value="away">Auswärts</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup exclusive size="small" value={analysisMode} onChange={(_, value) => value && setAnalysisMode(value)} sx={{ "& .MuiToggleButton-root": { px: 1, py: 0.3, fontSize: "0.68rem", fontWeight: 800, textTransform: "none" } }}>
          <ToggleButton value="table">Tabelle</ToggleButton>
          <ToggleButton value="strength">Strength</ToggleButton>
          <ToggleButton value="goals">Goals Sum</ToggleButton>
          <ToggleButton value="homeaway">Home / Away</ToggleButton>
          <ToggleButton value="factors">Faktoren</ToggleButton>
          <ToggleButton value="all">Alle Werte</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {analysisMode !== "table" && (
        <Box sx={{ overflowX: "auto", mb: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1 }} data-testid="team-season-metrics">
          <Box sx={{ display: "grid", gridTemplateColumns: `minmax(190px,1fr) repeat(${metricColumns.length}, minmax(92px,auto))`, gap: 0.5, px: 1, py: 0.7, bgcolor: "rgba(235,244,248,0.9)", fontSize: "0.68rem", fontWeight: 900, minWidth: Math.max(420, 210 + metricColumns.length * 105) }}>
            <span>Team</span>{metricColumns.map(([key, label]) => <span key={key}>{label}</span>)}
          </Box>
          {prepareCompactLeagueRows(actualRows, "actual", teamNames).map((row) => {
            const real = row.fixtureSettingValues;
            const stored = settingFor(row.teamId);
            const show = (value) => Number.isFinite(Number(value)) ? Number(value).toFixed(3) : "–";
            const cell = (key, value) => (
              <Box key={key}><b>{show(value)}</b>{stored && Number.isFinite(Number(stored[key])) && <Typography component="span" sx={{ display: "block", fontSize: "0.61rem", color: "text.secondary" }}>Setting {show(stored[key])}</Typography>}</Box>
            );
            return <Box key={row.teamId} sx={{ display: "grid", gridTemplateColumns: `minmax(190px,1fr) repeat(${metricColumns.length}, minmax(92px,auto))`, gap: 0.5, px: 1, py: 0.55, borderTop: "1px solid", borderColor: "divider", fontSize: "0.69rem", minWidth: Math.max(420, 210 + metricColumns.length * 105) }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontWeight: 800 }}>{teamLogos.get(row.teamId) && <img src={teamLogos.get(row.teamId)} alt="" width="22" height="22" style={{ objectFit: "contain" }} />}{row.name}</Box>
              {metricColumns.map(([key]) => cell(key, real[key]))}
            </Box>;
          })}
        </Box>
      )}
      {isComparison && (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={comparisonMetric}
          onChange={(_, metric) => metric && setComparisonMetric(metric)}
          sx={{ mb: 0.35, "& .MuiToggleButton-root": { px: 0.75, py: 0.1, fontSize: "0.65rem", fontWeight: 800, textTransform: "none" } }}
        >
          <ToggleButton value="points" onClick={() => setSort({ key: "delta", direction: "desc" })}>Pkt</ToggleButton>
          <ToggleButton value="difference" onClick={() => setSort({ key: "delta", direction: "desc" })}>GD</ToggleButton>
        </ToggleButtonGroup>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isComparison
            ? COMPARISON_TABLE_COLUMNS
            : CLASSIC_TABLE_COLUMNS,
          columnGap: 0.65,
          px: 0.8,
          py: 0.75,
          color: "rgba(255,255,255,.76)",
          bgcolor: "#25343e",
          borderRadius: "8px 8px 0 0",
          fontSize: "0.68rem",
          fontWeight: 900,
        }}
      >
        <span>#</span>
        <span aria-label="Logo" />
        <span>Team</span>
        {isComparison ? <>
          {sortLabel(VIEW_CONFIG[comparisonViews[0]].label, "leftValue")}
          {sortLabel(VIEW_CONFIG[comparisonViews[1]].label, "rightValue")}
          {sortLabel("Δ", "delta")}
        </> : <>
          {sortLabel(i18next.t("help.leagueWorkspace.playedShort") || "Sp", "played")}
          {sortLabel("S", "won")}
          {sortLabel("U", "draw")}
          {sortLabel("N", "lost")}
          {modeHeader(
            goalView,
            (event) => {
              const next = event.target.value;
              setGoalView(next);
              setSort(
                next === "goals"
                  ? { key: "points", direction: "desc" }
                  : {
                      key: next === "sum" ? "goalSum" : "goalAverage",
                      direction: "desc",
                    }
              );
            },
            [["goals", "Tore"], ["sum", "Summe"], ["average", "Average"]],
            goalSortKey,
            goalView !== "goals"
          )}
          {modeHeader(
            performanceView,
            (event) => {
              const next = event.target.value;
              setPerformanceView(next);
              setSort({ key: next === "difference" ? "difference" : "quotient", direction: "desc" });
            },
            [["difference", i18next.t("help.leagueWorkspace.differenceShort") || "Diff"], ["quotient", "Quotient"]],
            performanceSortKey
          )}
          {sortLabel(i18next.t("help.leagueWorkspace.pointsShort") || "Pkt", "points")}
        </>}
      </Box>
      {rows.map((row) => (
        <Box
          key={row.teamId}
          sx={{
            display: "grid",
            gridTemplateColumns: isComparison
              ? COMPARISON_TABLE_COLUMNS
              : CLASSIC_TABLE_COLUMNS,
            columnGap: 0.65,
            alignItems: "center",
            minHeight: 36,
            px: 0.8,
            borderTop: "1px solid",
            borderColor: "divider",
            borderLeft: "4px solid",
            borderLeftColor: ruleAccent(rulesByTeam.get(row.teamId)),
            bgcolor: selected.has(row.teamId)
              ? "rgba(255,181,99,0.28)"
              : row.displayRank % 2 === 0 ? "#f5f7f8" : "white",
            fontSize: "0.74rem",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          <Box title={(rulesByTeam.get(row.teamId) || []).map((rule) => rule?.provider_rule?.type?.name || rule?.rule_category).filter(Boolean).join(" · ")} sx={{ fontWeight: 800 }}>{row.displayRank}</Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            {teamLogos.get(row.teamId) && <img src={teamLogos.get(row.teamId)} alt="" width="24" height="24" style={{ objectFit: "contain" }} />}
          </Box>
          <Box
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: selected.has(row.teamId) ? 900 : 650,
            }}
          >
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }} title={row.name}>{row.name}</span>
          </Box>
          {isComparison ? <>
            <Box>{comparisonValue(row.leftValue, comparisonViews[0])}</Box>
            <Box>{comparisonValue(row.rightValue, comparisonViews[1])}</Box>
            <Box sx={{ fontWeight: 900 }}>{comparisonDelta(row.delta)}</Box>
          </> : <>
            <Box>{Number.isFinite(row.played) ? Math.round(row.played) : "–"}</Box>
            <Box>{tableValue(row.won, view)}</Box>
            <Box>{tableValue(row.draw, view)}</Box>
            <Box>{tableValue(row.lost, view)}</Box>
            <Box sx={{ whiteSpace: "nowrap" }}>
              {goalView === "goals"
                ? `${tableValue(row.goalsScored, view)}:${tableValue(row.goalsAgainst, view)}`
                : goalView === "sum"
                  ? numeric(row.goalSum, view === "actual" ? 0 : 2)
                  : numeric(row.goalAverage, 2)}
            </Box>
            <Box sx={{ fontWeight: 900, whiteSpace: "nowrap", bgcolor: "rgba(44,91,119,.08)", px: 0.45, py: 0.25, borderRadius: 0.7 }}>
              {performanceView === "difference"
                ? signedDifference(row.difference, view === "actual" ? 0 : 2)
                : row.quotient === Infinity
                  ? "∞"
                  : numeric(row.quotient, 2)}
            </Box>
            <Box sx={{ fontWeight: 950, fontSize: "0.8rem", bgcolor: "rgba(126,53,88,.10)", px: 0.55, py: 0.25, borderRadius: 0.7 }}>{tableValue(row.points, view)}</Box>
          </>}
        </Box>
      ))}
      {!rows.length && (
        <Typography variant="caption" color="text.secondary">
          {i18next.t("help.leagueWorkspace.noStoredTable")}
        </Typography>
      )}
    </Box>
  );
}

export function StrengthRanking({ rows = [], selectedTeamIds = [] }) {
  const selected = new Set(selectedTeamIds.map(Number));
  const ranking = [...rows]
    .filter((row) => Number.isFinite(Number(row?.total_strength)))
    .sort((a, b) => Number(b.total_strength) - Number(a.total_strength));

  return (
    <Box
      sx={{
        minWidth: 0,
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "#f7f9fc",
        color: "#17202b",
        border: "1px solid rgba(255,255,255,0.22)",
      }}
      data-testid="strength-ranking"
    >
      <Typography
        sx={{
          px: 0.8,
          py: 0.55,
          fontSize: "0.74rem",
          fontWeight: 900,
          bgcolor: "#eef2f7",
        }}
      >
        {i18next.t("help.leagueWorkspace.strengthRanking")} · {i18next.t("help.compilePreview.expectedGoalAverage")}
      </Typography>
      {ranking.map((item, index) => {
        const teamId = Number(item.team_id ?? item.teamId ?? item.id);
        return (
          <Box
            key={teamId}
            sx={{
              display: "grid",
              gridTemplateColumns: "24px minmax(0,1fr) 52px 52px",
              minHeight: 25,
              alignItems: "center",
              px: 0.65,
              borderTop: "1px solid rgba(0,0,0,0.08)",
              bgcolor: selected.has(teamId)
                ? "rgba(255,181,99,0.38)"
                : "transparent",
              fontSize: "0.7rem",
            }}
          >
            <Box>{index + 1}</Box>
            <Box
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontWeight: selected.has(teamId) ? 900 : 650,
              }}
            >
              {item.team_name || item.name || `Team ${teamId}`}
            </Box>
            <Box
              sx={{
                textAlign: "right",
                fontWeight: 900,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {Number(item.total_strength).toFixed(3)}
            </Box>
            <Box
              sx={{
                textAlign: "right",
                fontWeight: 800,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {Number.isFinite(Number(item.total_goals))
                ? Number(item.total_goals).toFixed(3)
                : "–"}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
